import { matchesUiText } from '../../i18n';
// src/features/collaboration/searchDataUtils.ts
import * as Y from 'yjs';
import { yXmlFragmentToProsemirrorJSON } from 'y-prosemirror';
import { YjsKeyManager } from './yjsKeyManager';
import { prepareTextForSearch, logNormalizationResult, stripHtmlTags } from './normalizationUtils';
import { generatePreview } from '../../utils/previewGenerator';

/**
 * 検索用データの型定義
 */
export interface SearchData {
  originalTitle: string;        // タイトル（漢字含む原文）
  normalizedTitle: string;      // タイトル（全ひらがな統一）
  originalContent: string;      // 本文（漢字含む原文）
  normalizedContent: string;    // 本文（全ひらがな統一）
  hashtags: string[];           // ページ内のハッシュタグ
  internalLinks: string[];      // このページがリンクしている内部ページID
  externalLinks: string[];      // 外部リンク（URL）配列
  originalContentTextCount: number; // 文字数
  DateOnPage?: {                // 日付関連フィールド
    date: any;                  // Timestamp（選択された日付）
    dateString: string;         // 表示用文字列
    normalizedDateString: string; // 検索用正規化文字列
    year: number;               // 年（検索・フィルタ用）
    month: number;              // 月（検索・フィルタ用）
    day: number;                // 日（検索・フィルタ用）
    weekday: string;            // 曜日（検索用）
  };
}

/**
 * プレビュー用データの型定義
 */
export interface PreviewData {
  title: string;                // プレビュー用タイトル（原文）
  thumbnailUrl: string | null;  // サムネイル画像（初期値は本文1枚目、変更可）
  content: any[];               // 本文冒頭や要約（最大300文字以内）のTipTap JSON配列
  structuredData: {             // OGPやカードUI用の構造化データ
    '@type': string;
    headline: string;
    thumbnailUrl: string | null;
    wordCount: number;
    dateCreated: string;
    dateModified: string;
    datePublished?: string;     // 選択された日付（ISO形式）
    eventDate?: string;         // イベント日付（schema.org対応）
    [key: string]: any;
  };
  previewMeta?: any;            // 新しいプレビューメタデータ（オプション）
  DateOnPage?: {                // 日付表示用フィールド
    date: any;                  // Timestamp
    displayText: string;        // 「2025年1月10日木曜日」
    shortText: string;          // 「1/10」
    isoString: string;          // ISO形式
  };
}

/**
 * Y.DocからContentJSONを抽出する
 */
export const extractContentJSONFromYdoc = (ydoc: Y.Doc): any => {
  try {
    // Y.Docの構造をデバッグ出力
    const shareKeys = Array.from(ydoc.share.keys());

    // 全フィールドの詳細調査
    const fieldDetails: Record<string, any> = {};
    shareKeys.forEach(key => {
      try {
        const field = ydoc.share.get(key);
        let length = 0;
        let hasContent = false;
        let toString = '';
        
        // 型ガードを使用して安全にlengthを取得
        if (field instanceof Y.Text || field instanceof Y.Array || field instanceof Y.XmlFragment) {
          length = field.length || 0;
        }
        
        // toStringメソッドがある場合は呼び出し
        if (field && typeof field.toString === 'function') {
          try {
            toString = field.toString();
          } catch (toStringError) {
            toString = '[toString error]';
          }
        }
        
        // コンテンツの有無を判定
        hasContent = length > 0 || toString.trim().length > 0;
        
        fieldDetails[key] = {
          type: field?.constructor?.name || typeof field,
          hasToString: typeof field?.toString === 'function',
          toString: toString.substring(0, 200), // より多く表示
          length,
          hasContent,
          isXmlFragment: field instanceof Y.XmlFragment,
          isText: field instanceof Y.Text,
          isArray: field instanceof Y.Array,
          isMap: field instanceof Y.Map
        };
      } catch (error) {
        fieldDetails[key] = { error: error instanceof Error ? error.message : String(error) };
      }
    });

    // グローバル固定の 'content' キーを優先使用（ensureContentXmlFragmentと一致）
    let contentKey = 'content'; // 最優先
    const boardId = (ydoc as any).boardId;

    // まず指定されたcontentキーで試す
    let contentFragment = ydoc.getXmlFragment(contentKey);

    // boardId固有のキーが空の場合、即座にデフォルトの'content'キーを試す
    if (!contentFragment || contentFragment.length === 0) {
      if (contentKey !== 'content' && ydoc.share.has('content')) {
        try {
          const defaultContentFragment = ydoc.getXmlFragment('content');

          if (defaultContentFragment && defaultContentFragment.length > 0) {
            contentFragment = defaultContentFragment;
            contentKey = 'content';
          }
        } catch (error) {
          console.warn('⚠️ デフォルトcontentキー試行失敗:', error);
        }
      }
    }

    if (!contentFragment || contentFragment.length === 0) {
      console.warn('⚠️ 指定キーでcontentフラグメントが見つかりません:', contentKey);

      // contentを含むフィールドを探す
      const contentKeys = shareKeys.filter(key => key.includes('content'));

      if (contentKeys.length > 0) {
        for (const key of contentKeys) {
          try {
            const alternativeFragment = ydoc.getXmlFragment(key);
            if (alternativeFragment && alternativeFragment.length > 0) {
              contentFragment = alternativeFragment;
              contentKey = key;
              break; // 最初に見つかったコンテンツを使用
            }
          } catch (error) {
            console.warn('⚠️ 代替フィールド試行失敗:', key, error);
          }
        }
      }

      // さらに全フィールドからコンテンツがあるものを探す
      if (!contentFragment || contentFragment.length === 0) {
        for (const key of shareKeys) {
          try {
            if (key.includes('meta') || key.includes('awareness')) continue; // メタデータフィールドは除外
            
            const field = ydoc.share.get(key);
            const fieldInfo = fieldDetails[key];
            
            if (fieldInfo?.hasContent) {
              // Y.Textフィールドかどうかチェック（TipTap JSONが格納されている可能性）
              if (field instanceof Y.Text && field.length > 0) {
                const textContent = field.toString();

                // TipTap JSONパースを試行
                try {
                  const parsedJSON = JSON.parse(textContent);
                  if (parsedJSON && typeof parsedJSON === 'object' && parsedJSON.type === 'doc') {
                    return parsedJSON;
                  }
                } catch (jsonParseError) {
                  // プレーンテキストの場合
                  if (textContent.trim()) {
                    return {
                      type: 'doc',
                      content: [
                        {
                          type: 'paragraph',
                          content: [
                            {
                              type: 'text',
                              text: textContent.trim()
                            }
                          ]
                        }
                      ]
                    };
                  }
                }
              }

              // XmlFragmentとして取得を試行
              try {
                const fragment = ydoc.getXmlFragment(key);
                if (fragment && fragment.length > 0) {
                  contentFragment = fragment;
                  contentKey = key;
                  break;
                }
              } catch (fragmentError) {
                console.warn('⚠️ XmlFragment取得失敗:', { key, error: fragmentError });
              }
            }
          } catch (error) {
            console.warn('⚠️ フィールド検索エラー:', key, error);
          }
        }
      }

      // 最後の手段：デフォルトの'content'キーを試す
      if (contentKey !== 'content') {
        try {
          contentFragment = ydoc.getXmlFragment('content');
          if (contentFragment) {
            const contentJSON = contentFragment.toJSON();
            return contentJSON;
          }
        } catch (error) {
          console.warn('⚠️ デフォルトcontentキー試行失敗:', error);
        }
      }

      return null;
    }

    // XmlFragmentからTipTap JSON構造を抽出
    try {
      const fragmentString = contentFragment.toString();

      // Y.XmlFragmentをTipTap JSONに変換する処理
      let contentJSON = null;

      // 最優先: y-prosemirror の正規変換（Y.XmlText の marks を正しく扱う）
      if (contentFragment.length > 0) {
        try {
          const json = yXmlFragmentToProsemirrorJSON(contentFragment);
          if (json?.type === 'doc' && Array.isArray(json.content) && json.content.length > 0) {
            contentJSON = json;
          }
        } catch (_) {}
      }

      // 方法1: toJSON()を試行（y-prosemirror が使えない場合のフォールバック）
      if (!contentJSON && contentFragment.length > 0) {
        try {
          const jsonResult = contentFragment.toJSON();
          if (jsonResult && typeof jsonResult === 'object') {
            if ((jsonResult as any).type === 'doc' && Array.isArray((jsonResult as any).content)) {
              contentJSON = jsonResult;
            } else if (Array.isArray((jsonResult as any).content)) {
              contentJSON = { type: 'doc', content: (jsonResult as any).content };
            }
          }
        } catch (_) {}
      }

      // 方法0: フラグメントを手動走査して getAttribute で全属性（width/height 含む）を取得
      const walkFragmentToTipTap = (frag: any): any[] => {
        const out: any[] = [];
        const len = typeof frag.length === 'number' ? frag.length : 0;
        for (let i = 0; i < len; i++) {
          try {
            const child = frag.get(i);
            if (!child) continue;
            const nodeName = typeof (child as any).nodeName === 'string' ? (child as any).nodeName : '';
            if (nodeName === 'paragraph' || nodeName === 'heading') {
              const content = walkFragmentToTipTap(child);
              const node: any = { type: nodeName === 'heading' ? 'heading' : 'paragraph', content };
              if (nodeName === 'heading') {
                const level = (child as any).getAttribute?.('level');
                node.attrs = level != null ? { level: Number(level) || 1 } : {};
              }
              out.push(node);
            } else if (nodeName === 'image' || nodeName === 'resizableImage') {
              const a: Record<string, string | null> = { src: null, alt: null, title: null };
              try {
                const getAttr = (name: string) => {
                  const v = (child as any).getAttribute?.(name);
                  return v != null ? String(v) : null;
                };
                a.src = getAttr('src');
                a.alt = getAttr('alt');
                a.title = getAttr('title');
                const w = getAttr('width');
                const h = getAttr('height');
                const s = getAttr('style');
                if (w) a.width = w;
                if (h) a.height = h;
                if (s) a.style = s;
              } catch (_) {}
              if (a.src) out.push({ type: 'image', attrs: a });
            } else if (nodeName === 'fileAttachment') {
              const a: Record<string, string | null> = {};
              try {
                ['fileId', 'title', 'mimeType', 'downloadURL', 'thumbnailURL'].forEach((k) => {
                  const v = (child as any).getAttribute?.(k);
                  if (v != null) a[k] = String(v);
                });
              } catch (_) {}
              if (a.fileId != null) out.push({ type: 'fileAttachment', attrs: a });
            } else if (nodeName === 'hashtag') {
              const tag = (child as any).getAttribute?.('tag');
              const inner = walkFragmentToTipTap(child);
              if (tag != null) {
                out.push({ type: 'hashtag', attrs: { tag: String(tag) }, content: inner });
              } else if (inner.length > 0) {
                out.push(...inner);
              }
            } else if (nodeName === 'autoLink') {
              const linkText = (child as any).getAttribute?.('linkText');
              const inner = walkFragmentToTipTap(child);
              if (linkText != null) {
                out.push({ type: 'autoLink', attrs: { linkText: String(linkText) }, content: inner });
              } else if (inner.length > 0) {
                out.push(...inner);
              }
            } else if (nodeName === 'videoEmbed') {
              const a: Record<string, string | null> = { src: null, width: null, height: null };
              try {
                ['src', 'width', 'height'].forEach((k) => {
                  const v = (child as any).getAttribute?.(k);
                  if (v != null) a[k as keyof typeof a] = String(v);
                });
              } catch (_) {}
              if (a.src) out.push({ type: 'videoEmbed', attrs: a });
            } else if (nodeName === 'thumbnail') {
              const a: Record<string, string | null> = {};
              try {
                ['pageTitle', 'linkText', 'position'].forEach((k) => {
                  const v = (child as any).getAttribute?.(k);
                  if (v != null) (a as any)[k] = String(v);
                });
              } catch (_) {}
              if (a.pageTitle != null || (a as any).linkText != null) {
                out.push({ type: 'thumbnail', attrs: a });
              }
            } else if (nodeName === 'link') {
              const inner = walkFragmentToTipTap(child);
              const attrs: Record<string, string> = {};
              try {
                const getAttr = (name: string) => {
                  const v = (child as any).getAttribute?.(name);
                  return v != null ? String(v) : undefined;
                };
                const href = getAttr('href');
                if (href) {
                  attrs.href = href;
                  const t = getAttr('target');
                  if (t) attrs.target = t;
                  const r = getAttr('rel');
                  if (r) attrs.rel = r;
                  const cls = getAttr('class');
                  if (cls && !cls.includes('pointer-events-none')) attrs.class = cls;
                  else attrs.class = 'text-blue-600 underline cursor-pointer';
                }
              } catch (_) {}
              if (attrs.href) out.push({ type: 'link', attrs, content: inner });
              else if (inner.length > 0) out.push(...inner);
            } else if (nodeName === 'table' || nodeName === 'tableRow' || nodeName === 'tableCell' || nodeName === 'tableHeader') {
              // 表は下の汎用フォールバックに落とすと table ラッパが消えて子だけ親へ巻き上がるため、
              // ノード名を保ったまま再帰する
              const inner = walkFragmentToTipTap(child);
              const node: any = { type: nodeName, content: inner };
              if (nodeName === 'tableCell' || nodeName === 'tableHeader') {
                const attrs: Record<string, any> = {};
                try {
                  const colspan = Number((child as any).getAttribute?.('colspan'));
                  const rowspan = Number((child as any).getAttribute?.('rowspan'));
                  attrs.colspan = Number.isFinite(colspan) && colspan > 0 ? colspan : 1;
                  attrs.rowspan = Number.isFinite(rowspan) && rowspan > 0 ? rowspan : 1;
                  const colwidth = (child as any).getAttribute?.('colwidth');
                  if (Array.isArray(colwidth) && colwidth.length > 0) attrs.colwidth = colwidth;
                } catch (_) {}
                node.attrs = attrs;
                // 空セルでも段落を1つ持たせる（TipTap のスキーマ上 content が必須）
                if (inner.length === 0) node.content = [{ type: 'paragraph' }];
              }
              out.push(node);
            } else if (child instanceof Y.XmlText || (child && typeof (child as any).toString === 'function' && (nodeName === '' || nodeName === 'text' || nodeName == null))) {
              const delta = (child as any).toDelta?.();
              if (delta && Array.isArray(delta)) {
                for (const d of delta) {
                  const txt = typeof d.insert === 'string' ? d.insert : '';
                  const attrs = d.attributes;
                  const marks: any[] = [];
                  if (attrs && typeof attrs === 'object') {
                    for (const key of Object.keys(attrs)) {
                      const type = key.replace(/--[a-zA-Z0-9+/=]+$/, '');
                      const markAttrs = attrs[key];
                      if (markAttrs && typeof markAttrs === 'object') {
                        marks.push({ type, attrs: markAttrs });
                      }
                    }
                  }
                  if (txt) out.push(marks.length > 0 ? { type: 'text', text: txt, marks } : { type: 'text', text: txt });
                }
              } else {
                const txt = (child as any).toString?.() ?? '';
                if (txt.trim()) out.push({ type: 'text', text: txt });
              }
            } else if (nodeName && typeof (child as any).length === 'number') {
              const inner = walkFragmentToTipTap(child);
              if (inner.length > 0) out.push(...inner);
            }
          } catch (_) {}
        }
        return out;
      };
      if (!contentJSON && contentFragment.length > 0) {
        try {
          const topLevel = walkFragmentToTipTap(contentFragment);
          if (topLevel.length > 0) {
            contentJSON = { type: 'doc', content: topLevel };
          }
        } catch (_) {}
      }

      // 方法1の続き: toJSON()が方法0より前に失敗していた場合に再試行
      if (!contentJSON) {
        try {
          const jsonResult = contentFragment.toJSON();

          if (jsonResult && typeof jsonResult === 'object') {
            contentJSON = jsonResult;
          }
        } catch (jsonError) {
          console.warn('⚠️ toJSON()失敗:', jsonError instanceof Error ? jsonError.message : String(jsonError));
        }
      }

      // 方法2: Y.XmlFragmentの子要素を直接TipTap JSON形式に変換
      if (!contentJSON && contentFragment.length > 0) {
        const paragraphs: any[] = [];

        // 各子要素を処理
        for (let i = 0; i < contentFragment.length; i++) {
          const child = contentFragment.get(i);
          
          if (child && typeof child.toString === 'function') {
            const childText = child.toString();
            
            // HTMLタグが含まれている場合は、段落として処理
            if (childText.includes('<paragraph>')) {
              // HTMLタグベースの構造からTipTap JSONを構築
              const paragraphTexts = childText.split('<paragraph>').filter((p: string) => p.trim());

              for (const pText of paragraphTexts) {
                // 画像タグをチェック（img と image の両方に対応）
                const imageMatches: RegExpMatchArray[] = Array.from(pText.matchAll(/<image[^>]*>/g));

                if (imageMatches.length === 0) {
                  // imgタグの存在をチェック
                  if (pText.includes('img')) {}
                  if (pText.includes('<img')) {}

                  // data:image/ を直接検索（Base64画像）
                  const dataImageMatch = pText.match(/data:image\/[^"'\s]+/);
                  if (dataImageMatch) {
                    // Base64画像を直接imageノードとして追加
                    paragraphs.push({
                      type: 'paragraph',
                      content: [
                        {
                          type: 'image',
                          attrs: {
                            src: dataImageMatch[0],
                            alt: null,
                            title: null
                          }
                        }
                      ]
                    });
                  }
                }

                for (const match of imageMatches) {
                  const imageTag = match[0];

                  // src属性を抽出
                  const srcMatch = imageTag.match(/src="([^"]+)"/);
                  // alt属性を抽出
                  const altMatch = imageTag.match(/alt="([^"]*)"/);
                  const widthMatch = imageTag.match(/width="([^"]*)"/);
                  const heightMatch = imageTag.match(/height="([^"]*)"/);
                  const styleMatch = imageTag.match(/style="([^"]*)"/);

                  if (srcMatch) {
                    const src = srcMatch[1];
                    const alt = altMatch ? altMatch[1] : '';

                    // ユーザーアイコンを除外
                    if (!matchesUiText(alt, "User icon")) {
                      const attrs: Record<string, string | null> = {
                        src: src,
                        alt: alt || null,
                        title: null
                      };
                      if (widthMatch && widthMatch[1]) attrs.width = widthMatch[1];
                      if (heightMatch && heightMatch[1]) attrs.height = heightMatch[1];
                      if (styleMatch && styleMatch[1]) attrs.style = styleMatch[1];
                      paragraphs.push({
                        type: 'paragraph',
                        content: [
                          {
                            type: 'image',
                            attrs
                          }
                        ]
                      });
                    } else {}
                  }
                }

                const cleanText = pText.replace(/<[^>]*>/g, '').replace('</paragraph>', '').trim();
                if (cleanText) {
                  paragraphs.push({
                    type: 'paragraph',
                    content: [
                      {
                        type: 'text',
                        text: cleanText
                      }
                    ]
                  });
                }
              }
            } else if (childText.trim()) {
              // 通常のテキストとして処理
              paragraphs.push({
                type: 'paragraph',
                content: [
                  {
                    type: 'text',
                    text: childText.trim()
                  }
                ]
              });
            }
          }
        }

        if (paragraphs.length > 0) {
          contentJSON = {
            type: 'doc',
            content: paragraphs
          };

          // 構築されたJSONに画像ノードが含まれているかチェック
          const hasImageNodes = JSON.stringify(contentJSON).includes('"type":"image"');
        }
      }

      // 方法3: 最後の手段として、toString()からシンプルなJSON構造を生成
      if (!contentJSON) {
        const textContent = contentFragment.toString();
        if (textContent.trim()) {
          // ハッシュタグを含む場合は特別な処理を行う
          if (textContent.includes('<hashtag')) {
            contentJSON = parseXmlToTipTapJSON(textContent);
          } else {
            // HTMLタグを除去してシンプルな構造を作成
            const cleanText = stripHtmlTags(textContent).trim();

            contentJSON = {
              type: 'doc',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      type: 'text',
                      text: cleanText
                    }
                  ]
                }
              ]
            };
          }
        }
      }

      if (contentJSON) {
        return contentJSON;
      } else {
        console.warn('⚠️ TipTap JSON構造の生成に失敗');
        return null;
      }
    } catch (error) {
      console.error('❌ TipTap JSON変換エラー:', error);
      return null;
    }
  } catch (error) {
    console.error('❌ Y.DocからContentJSON抽出エラー:', error);
    return null;
  }
};

const TABLE_CELL_TYPES = ['tableCell', 'tableHeader'];

/**
 * 表のセル内テキストを 1 行に正規化する。
 * Markdown の行構造を壊さないよう、改行を空白へ落としパイプをエスケープする。
 */
const normalizeTableCellText = (text: string): string =>
  text
    .replace(/\r?\n/g, ' ')
    .replace(/\|/g, '\\|')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * TipTap の table ノードを Markdown のパイプ表へ変換する。
 *
 * RAG の埋め込み・全文検索へ渡すテキストで表構造を保つのが目的。
 * セルを区切りなしに連結すると「外傷負傷原因届外傷の際に…」のように
 * どの値がどの列のものか判別できなくなり、回答精度が落ちる。
 */
const convertTableToMarkdown = (tableNode: any, extractText: (node: any) => string): string => {
  const rows: string[][] = [];
  let firstRowIsHeader = false;

  const rowNodes = Array.isArray(tableNode?.content) ? tableNode.content : [];

  rowNodes.forEach((rowNode: any, rowIndex: number) => {
    if (!rowNode || !Array.isArray(rowNode.content)) return;

    const cells: string[] = [];
    let headerCellCount = 0;
    let cellCount = 0;

    rowNode.content.forEach((cellNode: any) => {
      if (!cellNode || !TABLE_CELL_TYPES.includes(cellNode.type)) return;

      cellCount += 1;
      if (cellNode.type === 'tableHeader') headerCellCount += 1;

      cells.push(normalizeTableCellText(extractText(cellNode)));

      // colspan がある場合は列位置がずれないよう空セルで埋める
      const colspan = Number(cellNode.attrs?.colspan) || 1;
      for (let i = 1; i < colspan; i += 1) {
        cells.push('');
      }
    });

    if (cells.length === 0) return;

    if (rowIndex === 0 && cellCount > 0 && headerCellCount === cellCount) {
      firstRowIsHeader = true;
    }

    rows.push(cells);
  });

  if (rows.length === 0) return '';

  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
  const toLine = (cells: string[]): string => {
    const padded = [...cells];
    while (padded.length < columnCount) padded.push('');
    return `| ${padded.join(' | ')} |`;
  };

  const separator = `| ${new Array(columnCount).fill('---').join(' | ')} |`;

  // ヘッダー行が無い表でも区切り行を出し、LLM が表として認識できる形にする
  const lines = firstRowIsHeader
    ? [toLine(rows[0]), separator, ...rows.slice(1).map(toLine)]
    : [toLine(new Array(columnCount).fill('')), separator, ...rows.map(toLine)];

  // 前後を空行で挟み、周囲の本文と混ざらないようにする
  return `\n${lines.join('\n')}\n\n`;
};

/**
 * contentJSONからテキストコンテンツを抽出する関数
 */
export const extractTextFromContentJSON = (contentJSON: any): string => {
  if (!contentJSON) return '';
  
  try {
    if (typeof contentJSON === 'string') {
      const parsed = JSON.parse(contentJSON);
      return extractTextFromContentJSON(parsed);
    }
    
    if (Array.isArray(contentJSON)) {
      // 配列要素がparagraphタイプの場合は改行で結合
      const results = contentJSON.map((item: any) => {
        const result = extractTextFromContentJSON(item);
        // paragraphタイプの場合は既に改行が含まれているので、そのまま返す
        return result;
      });
      const finalResult = results.join('');
      
      // デバッグ用：改行を含むコンテンツの場合はログ出力
      if (finalResult.includes('改行')) {}
      
      return finalResult;
    }
    
    if (typeof contentJSON === 'object' && contentJSON !== null) {
      // textタイプの場合はテキストのみを抽出
      if (contentJSON.type === 'text' && contentJSON.text) {
        return contentJSON.text;
      }
      
      // autoLinkタイプの場合はlinkText属性を抽出
      if (contentJSON.type === 'autoLink' && contentJSON.attrs && contentJSON.attrs.linkText) {
        return contentJSON.attrs.linkText;
      }
      
      // hardBreakタイプの場合は改行を追加
      if (contentJSON.type === 'hardBreak') {
        return '\n';
      }
      
      // paragraphタイプの場合は改行を追加（段落の区切り）
      if (contentJSON.type === 'paragraph') {
        if (contentJSON.content && Array.isArray(contentJSON.content) && contentJSON.content.length > 0) {
          const paragraphContent = contentJSON.content.map((item: any) => extractTextFromContentJSON(item)).join('');
          // 内容がある段落の場合は改行を追加
          return paragraphContent + '\n';
        }
        // 空の段落の場合は改行のみ
        return '\n';
      }

      // tableタイプはMarkdownのパイプ表として出力（RAG・検索で列の対応を保つ）
      if (contentJSON.type === 'table') {
        return convertTableToMarkdown(contentJSON, extractTextFromContentJSON);
      }
      
      // contentプロパティがある場合は再帰的に処理
      if (contentJSON.content && Array.isArray(contentJSON.content)) {
        return contentJSON.content.map((item: any) => extractTextFromContentJSON(item)).join('');
      }
      
      // その他のプロパティは無視（type, attrsなどのメタデータ）
      return '';
    }
    
    return '';
  } catch (error) {
    console.error('contentJSONの解析エラー:', error);
    return '';
  }
};

/**
 * XmlFragmentの文字列をTipTap JSONに変換する関数
 */
const parseXmlToTipTapJSON = (xmlText: string): any => {
  try {
    // 段落ごとに分割
    const paragraphs = xmlText.split('</paragraph>').filter(p => p.trim());

    const content: any[] = [];

    paragraphs.forEach((paragraphText, index) => {
      // paragraph開始タグを除去
      const cleanParagraphText = paragraphText.replace('<paragraph>', '').trim();

      if (!cleanParagraphText) {
        return;
      }

      const paragraphContent: any[] = [];

      // ハッシュタグを検出して変換
      const hashtagRegex = /<hashtag tag="([^"]+)"><\/hashtag>/g;
      let lastIndex = 0;
      let match;

      while ((match = hashtagRegex.exec(cleanParagraphText)) !== null) {
        const beforeText = cleanParagraphText.substring(lastIndex, match.index);
        const tagName = match[1];

        // ハッシュタグ前のテキストがあれば追加（空白のみの場合は除外）
        if (beforeText && beforeText.trim()) {
          paragraphContent.push({
            type: 'text',
            text: beforeText
          });
        }

        // ハッシュタグノードを追加
        paragraphContent.push({
          type: 'hashtag',
          attrs: {
            tag: tagName
          }
        });

        lastIndex = match.index + match[0].length;
      }

      // 残りのテキストがあれば追加（空白のみの場合は除外）
      const remainingText = cleanParagraphText.substring(lastIndex);
      if (remainingText && remainingText.trim()) {
        paragraphContent.push({
          type: 'text',
          text: remainingText
        });
      }

      // 段落にコンテンツがない場合は空のテキストノードを追加
      if (paragraphContent.length === 0) {
        paragraphContent.push({
          type: 'text',
          text: ''
        });
      }

      // 段落が空でない場合のみ追加
      if (paragraphContent.length > 0) {
        content.push({
          type: 'paragraph',
          content: paragraphContent
        });
      }
    });

    const result = {
      type: 'doc',
      content: content
    };

    return result;
  } catch (error) {
    console.error('❌ parseXmlToTipTapJSON エラー:', error);
    
    // エラー時は元のシンプルな構造にフォールバック
    const cleanText = stripHtmlTags(xmlText).trim();
    return {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: cleanText
            }
          ]
        }
      ]
    };
  }
};

/**
 * contentJSONからハッシュタグを抽出する関数
 */
export const extractHashtagsFromContentJSON = (contentJSON: any): string[] => {
  const hashtags: string[] = [];

  if (!contentJSON) {
    return hashtags;
  }

  const traverse = (nodes: any[], depth: number = 0) => {
    if (!Array.isArray(nodes)) {
      return;
    }

    nodes.forEach((node, index) => {
      // hashtagタイプの場合は tag属性を取得
      if (node.type === 'hashtag') {
        if (node.attrs && node.attrs.tag) {
          hashtags.push(node.attrs.tag);
        } else {
          console.warn('⚠️ ハッシュタグノードにtag属性がありません:', node);
        }
      }

      // 子ノードも再帰的に処理
      if (node.content && Array.isArray(node.content)) {
        traverse(node.content, depth + 1);
      }
    });
  };

  try {
    // docノードの場合はcontentを処理
    if (contentJSON.type === 'doc' && contentJSON.content) {
      traverse(contentJSON.content, 0);
    } else if (Array.isArray(contentJSON)) {
      traverse(contentJSON, 0);
    } else {
      console.warn('⚠️ 予期しないContentJSON構造:', {
        type: contentJSON.type,
        hasContent: !!contentJSON.content,
        structure: JSON.stringify(contentJSON, null, 2).substring(0, 500)
      });
    }
  } catch (error) {
    console.error('❌ ハッシュタグ抽出エラー:', error);
    console.error('❌ エラー時のcontentJSON:', JSON.stringify(contentJSON, null, 2).substring(0, 500));
  }

  const uniqueHashtags = Array.from(new Set(hashtags));

  return uniqueHashtags;
};

/**
 * contentJSONから最初の画像URLを抽出する関数
 */
const extractFirstImageFromContentJSON = (contentJSON: any): string | null => {
  if (!contentJSON) return null;

  const traverse = (nodes: any[], depth = 0): string | null => {
    if (!Array.isArray(nodes)) return null;

    for (const node of nodes) {
      // imageタイプまたはinlineImageタイプの場合はsrc属性を取得
      if ((node.type === 'image' || node.type === 'inlineImage') && node.attrs && node.attrs.src) {
        // ユーザーアイコン画像（alt="ユーザーアイコン"）を除外
        if (matchesUiText(node.attrs.alt, "User icon")) {
          continue; // 次のノードへ
        }

        return node.attrs.src;
      }

      // 子ノードも再帰的に処理
      if (node.content && Array.isArray(node.content)) {
        const result = traverse(node.content, depth + 1);
        if (result) return result;
      }
    }

    return null;
  };

  try {
    // docノードの場合はcontentを処理
    if (contentJSON.type === 'doc' && contentJSON.content) {
      return traverse(contentJSON.content);
    } else if (Array.isArray(contentJSON)) {
      return traverse(contentJSON);
    }
  } catch (error) {
    console.error('画像抽出エラー:', error);
  }

  return null;
};

/**
 * contentJSONから内部リンクと外部リンクを抽出する関数
 */
const extractLinksFromContentJSON = (contentJSON: any): { internalLinks: string[], externalLinks: string[] } => {
  const internalLinks: string[] = [];
  const externalLinks: string[] = [];

  if (!contentJSON) {
    return { internalLinks, externalLinks };
  }

  const traverse = (nodes: any[], depth: number = 0) => {
    if (!Array.isArray(nodes)) {
      return;
    }

    nodes.forEach((node, index) => {
      // 内部リンク（autoLinkタイプ）
      if (node.type === 'autoLink' && node.attrs && node.attrs.linkText) {
        internalLinks.push(node.attrs.linkText);
      }

      // 外部リンク（linkタイプ - ノードとして）
      if (node.type === 'link' && node.attrs && node.attrs.href) {
        externalLinks.push(node.attrs.href);
      }

      // 外部リンク（linkマークとして）- TipTapのLink拡張用
      if (node.marks && Array.isArray(node.marks)) {
        node.marks.forEach((mark: any, markIndex: number) => {
          if (mark.type === 'link' && mark.attrs && mark.attrs.href) {
            externalLinks.push(mark.attrs.href);
          }
        });
      }

      // 動画埋め込み（videoEmbedタイプ）- YouTubeなどの動画URL
      if (node.type === 'videoEmbed' && node.attrs && node.attrs.src) {
        externalLinks.push(node.attrs.src);
      }

      // 子ノードも再帰的に処理
      if (node.content && Array.isArray(node.content)) {
        traverse(node.content, depth + 1);
      }
    });
  };

  try {
    // docノードの場合はcontentを処理
    if (contentJSON.type === 'doc' && contentJSON.content) {
      traverse(contentJSON.content);
    } else if (Array.isArray(contentJSON)) {
      traverse(contentJSON);
    } else {}
  } catch (error) {
    console.error('❌ [Link抽出] リンク抽出エラー:', error);
  }

  const result = {
    internalLinks: Array.from(new Set(internalLinks)),
    externalLinks: Array.from(new Set(externalLinks))
  };

  return result;
};

/**
 * TipTap JSON構造からプレビュー用のコンテンツを抽出する関数（文字数制限なし）
 */
const extractPreviewContentFromContentJSON = (contentJSON: any, maxLength: number = 9999): any[] => {
  if (!contentJSON) return [];

  // 実際のcontentJSONの内容を確認
  let actualContent: any[] = [];

  if (contentJSON.type === 'doc' && Array.isArray(contentJSON.content)) {
    actualContent = contentJSON.content;
  } else if (Array.isArray(contentJSON)) {
    actualContent = contentJSON;
  } else {
    console.warn('⚠️ 予期しないcontentJSON構造:', contentJSON);
    return [];
  }

  return actualContent;
};

/**
 * プレビューデータを生成する関数（レガシー互換性維持）
 * 注意: 新しい実装では generatePreview 関数を使用することを推奨
 */
const generatePreviewData = (contentJSON: any, title: string): PreviewData => {
  // 最初の画像URLを抽出
  const thumbnailUrl = extractFirstImageFromContentJSON(contentJSON);

  // プレビュー用コンテンツを抽出（文字数制限なし）
  const content = extractPreviewContentFromContentJSON(contentJSON, 9999);

  // 新しいプレビュー生成関数を試行（フォールバック付き）
  let previewMeta = null;
  try {
    
    // contentJSONをASTNode配列として扱う
    let ast: any[] = [];
    if (contentJSON?.type === 'doc' && Array.isArray(contentJSON.content)) {
      ast = contentJSON.content;
    } else if (Array.isArray(contentJSON)) {
      ast = contentJSON;
    }

    if (ast.length > 0) {
      previewMeta = generatePreview(ast);
    }
  } catch (error) {
    console.warn('⚠️ 新しいプレビュー生成関数の使用に失敗、従来方式を使用:', error);
  }

  // 構造化データ（OGP用など）
  const structuredData = {
    '@type': 'Article',
    headline: title,
    thumbnailUrl: thumbnailUrl,
    wordCount: content.length,
    dateCreated: new Date().toISOString(),
    dateModified: new Date().toISOString()
  };

  const basePreviewData = {
    title: title,
    thumbnailUrl: thumbnailUrl,
    content: content,
    structuredData: structuredData
  };

  // 新しいプレビューメタデータがある場合は統合
  if (previewMeta) {
    return {
      ...basePreviewData,
      previewMeta: previewMeta,
      // contentフィールドをクリーンなテキストで更新
      content: previewMeta.previewText ? [{
        type: 'paragraph',
        content: [{
          type: 'text',
          text: previewMeta.previewText
        }]
      }] : content
    };
  }

  return basePreviewData;
};

/**
 * Y.Docから検索データとプレビューデータを生成する統合関数
 */
export const generateSearchAndPreviewData = (ydoc: Y.Doc, title: string, editorContentJSON?: any): { searchData: SearchData | null, previewData: PreviewData | null } => {
  try {
    // 1. ContentJSONを取得 - エディタから提供された場合はそれを優先使用
    let contentJSON = editorContentJSON;

    if (!contentJSON) {
      contentJSON = extractContentJSONFromYdoc(ydoc);
    } else {}

    if (!contentJSON) {
      console.warn('⚠️ エディタコンテンツの取得に失敗しました - タイトルのみでデータ生成');
      
      // タイトルの正規化処理
      const titleResult = prepareTextForSearch(title);
      logNormalizationResult(titleResult.original, titleResult.normalized, 'title');
      
      // コンテンツが空の場合でもタイトルベースのデータを生成
      const searchData: SearchData = {
        originalTitle: titleResult.original,
        normalizedTitle: titleResult.normalized,
        originalContent: '',
        normalizedContent: '',
        hashtags: [],
        internalLinks: [],
        externalLinks: [],
        originalContentTextCount: 0
      };
      
      const previewData: PreviewData = {
        title: title,
        thumbnailUrl: null,
        content: [],
        structuredData: {
          '@type': 'Article',
          headline: title,
          thumbnailUrl: null,
          wordCount: 0,
          dateCreated: new Date().toISOString(),
          dateModified: new Date().toISOString()
        }
      };
      
      return { searchData, previewData };
    }

    // 2. テキストコンテンツを抽出
    const originalContent = extractTextFromContentJSON(contentJSON);

    // 3. タイトルとコンテンツの正規化処理
    const titleResult = prepareTextForSearch(title);
    const contentResult = prepareTextForSearch(originalContent);

    // 正規化結果をログ出力（デバッグ用）
    logNormalizationResult(titleResult.original, titleResult.normalized, 'title');
    logNormalizationResult(contentResult.original, contentResult.normalized, 'content');

    const hashtags = extractHashtagsFromContentJSON(contentJSON);

    // 5. リンクを抽出
    const { internalLinks, externalLinks } = extractLinksFromContentJSON(contentJSON);

    // 6. 文字数をカウント
    const originalContentTextCount = originalContent.length;

    // 検索データを生成
    const searchData: SearchData = {
      originalTitle: titleResult.original,
      normalizedTitle: titleResult.normalized,
      originalContent: contentResult.original,
      normalizedContent: contentResult.normalized,
      hashtags,
      internalLinks,
      externalLinks,
      originalContentTextCount
    };

    // プレビューデータを生成
    const previewData = generatePreviewData(contentJSON, title);

    return { searchData, previewData };
  } catch (error) {
    console.error('❌ 検索・プレビューデータ生成エラー:', error);
    return { searchData: null, previewData: null };
  }
};

/**
 * Y.Docから検索用データを生成する関数（後方互換性のため）
 */
export const generateSearchDataFromYdoc = (ydoc: Y.Doc, title: string): SearchData | null => {
  try {
    // 1. ContentJSONを取得
    const contentJSON = extractContentJSONFromYdoc(ydoc);
    if (!contentJSON) {
      console.warn('⚠️ エディタコンテンツの取得に失敗しました - タイトルのみでデータ生成');
      
      // タイトルの正規化処理
      const titleResult = prepareTextForSearch(title);
      logNormalizationResult(titleResult.original, titleResult.normalized, 'title');
      
      // コンテンツが空の場合でもタイトルベースの検索データを生成
      return {
        originalTitle: titleResult.original,
        normalizedTitle: titleResult.normalized,
        originalContent: '', // 空のコンテンツ
        normalizedContent: '', // 空のコンテンツ（正規化）
        hashtags: [],
        internalLinks: [],
        externalLinks: [],
        originalContentTextCount: 0
      };
    }

    // 2. テキストコンテンツを抽出
    const originalContent = extractTextFromContentJSON(contentJSON);

    // 3. タイトルとコンテンツの正規化処理
    const titleResult = prepareTextForSearch(title);
    const contentResult = prepareTextForSearch(originalContent);

    // 正規化結果をログ出力（デバッグ用）
    logNormalizationResult(titleResult.original, titleResult.normalized, 'title');
    logNormalizationResult(contentResult.original, contentResult.normalized, 'content');

    const hashtags = extractHashtagsFromContentJSON(contentJSON);

    // 5. リンクを抽出
    const { internalLinks, externalLinks } = extractLinksFromContentJSON(contentJSON);

    // 6. 文字数をカウント
    const originalContentTextCount = originalContent.length;

    const searchData: SearchData = {
      originalTitle: titleResult.original,
      normalizedTitle: titleResult.normalized,
      originalContent: contentResult.original,
      normalizedContent: contentResult.normalized,
      hashtags,
      internalLinks,
      externalLinks,
      originalContentTextCount
    };

    return searchData;
  } catch (error) {
    console.error('❌ 検索データ生成エラー:', error);
    return null;
  }
};
